import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component - Handles meta tags, Open Graph, Twitter Cards, and JSON-LD structured data
 */
const SEO = ({
  title = 'TweePorter - Convert Twine Stories to Script-Free HTML',
  description = 'Turn Twine interactive stories into clean HTML that works inside GoDaddy, Wix, Squarespace, and Shopify—no scripts, plugins, or hosting required.',
  keywords = 'Twine, Twee, interactive stories, choose your own adventure, HTML converter, GoDaddy, Wix, Squarespace, Shopify, no-code storytelling',
  canonicalUrl,
  ogImage = 'https://tweeporter.ca/og-image.png',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  structuredData,
  children
}) => {
  const siteUrl = 'https://tweeporter.ca';
  const fullUrl = canonicalUrl ? `${siteUrl}${canonicalUrl}` : siteUrl;

  // Default WebSite structured data
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'TweePorter',
    url: siteUrl,
    description: 'Convert Twine/Twee interactive stories into script-free HTML for any website builder',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/compiler?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  // Software Application structured data
  const softwareAppData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'TweePorter',
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'Web Browser',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    },
    description: 'A free online tool that converts Twine/Twee interactive story code into clean, script-free HTML that works on restrictive website builders like GoDaddy, Wix, Squarespace, and Shopify.',
    featureList: [
      'Convert Twee/SugarCube code to HTML',
      'No JavaScript required in output',
      'Works on GoDaddy, Wix, Squarespace, Shopify',
      'Visual Story Planner with AI assistance',
      'Multiple layout options',
      'Mobile responsive output'
    ]
  };

  // Organization structured data
  const organizationData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'TweePorter',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    sameAs: []
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="TweePorter" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="TweePorter" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Structured Data - Default */}
      <script type="application/ld+json">
        {JSON.stringify(defaultStructuredData)}
      </script>
      
      {/* Structured Data - Software Application */}
      <script type="application/ld+json">
        {JSON.stringify(softwareAppData)}
      </script>
      
      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify(organizationData)}
      </script>
      
      {/* Custom Structured Data (e.g., FAQPage) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {children}
    </Helmet>
  );
};

/**
 * FAQ Schema Generator - Creates FAQPage structured data
 */
export const generateFAQSchema = (faqs) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
};

export default SEO;
